import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Label,
} from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";

import type { RootState } from "store";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import RoleMenuContainer from "./rolemenucontainer";
import RoleMenuForm from "./rolemenuform";
import { handleValidDate, handleValidTime } from "helpers/common_helper";

import NewDeleteModal from "Components/Common/NewDeleteModal";
import { toast } from "react-toastify";

import {
  addRoleMenuThunk,
  deleteRoleMenuThunk,
  fetchRoleMenusThunk,
  fetchMenusThunk,
  fetchRolesThunk,
} from "slices/RBAC/thunk";

import { Link, Navigate, useNavigate } from "react-router-dom";
import { usePermissions } from "../../../hooks/usePermissions";
import { PermissionEnum } from "../../../Components/constants/permissions";

const selectRBACState = (state: RootState) => (state as any).rbac;

const rbacSelector = createSelector(selectRBACState, (rbac) => ({
  roleMenus: rbac.roleMenus,
  menus: rbac.menus,
  roles: rbac.roles,
  loading: rbac.loading,
}));

const RoleMenuMasterPage: React.FC = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const { roleMenus, menus, roles, loading } = useSelector(rbacSelector);
  const userPermissions = usePermissions();

  const roleMenuList = roleMenus || [];
  const [selectedMenuId, setSelectedMenuId] = useState<number | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRoleMenuId, setSelectedRoleMenuId] = useState<number | null>(null);

  useEffect(() => {
    // Fetch menus and roles on mount
    dispatch(fetchMenusThunk({
      parentid: 0,
      moduleid: 0,
      menuid: 0,
      status: 0,
    }));
    dispatch(fetchRolesThunk());
  }, [dispatch]);

  useEffect(() => {
    // Fetch role menus when filters change
    dispatch(fetchRoleMenusThunk({
      menuid: selectedMenuId || 0,
      roleid: selectedRoleId || 0,
    }));
  }, [dispatch, selectedMenuId, selectedRoleId]);

  // Filter to show only active menus
  const activeMenus = React.useMemo(() => {
    if (!Array.isArray(menus)) return [];
    return menus.filter((menu: any) => 
      menu.status?.toLowerCase() === "active"
    );
  }, [menus]);

  const menuOptions = activeMenus.map((menu) => ({
    label: menu.title || menu.menuname,
    value: menu.menuid,
  }));

  // Filter to show only active roles
  const activeRoles = React.useMemo(() => {
    if (!Array.isArray(roles)) return [];
    return roles.filter((role: any) => 
      role.status?.toLowerCase() === "active"
    );
  }, [roles]);

  const roleOptions = activeRoles.map((role) => ({
    label: role.rolename,
    value: role.roleid,
  }));

  const handleMenuChange = (selectedOption: { value: number; label: string } | null) => {
    if (selectedOption) {
      setSelectedMenuId(selectedOption.value);
    } else {
      setSelectedMenuId(null);
    }
  };

  const handleRoleChange = (selectedOption: { value: number; label: string } | null) => {
    if (selectedOption) {
      setSelectedRoleId(selectedOption.value);
    } else {
      setSelectedRoleId(null);
    }
  };

  const navigate = useNavigate();

  const handleDeleteClick = (id: number) => {
    setSelectedRoleMenuId(id);
    setDeleteModalOpen(true);
  };

  // Helper function to check for duplicate role-menu combination
  const checkDuplicateRoleMenu = useCallback((roleid: number, menuid: number): boolean => {
    if (!roleid || !menuid) return false;
    
    const exists = roleMenuList.some(
      (roleMenu: any) =>
        Number(roleMenu.roleid) === Number(roleid) &&
        Number(roleMenu.menuid) === Number(menuid)
    );
    
    return exists;
  }, [roleMenuList]);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        roleid: Yup.number().required("Role is required"),
        menuid: Yup.number().required("Menu is required"),
      }),
    []
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      roleid: "",
      menuid: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const roleId = Number(values.roleid) || 0;
      const menuId = Number(values.menuid) || 0;

      // Additional validation check before submission (safety measure)
      if (checkDuplicateRoleMenu(roleId, menuId)) {
        toast.error("This role-menu combination already exists. Please select a different combination.");
        return;
      }

      // Close modal immediately
      setIsModalOpen(false);
      resetForm();

      try {
        const res = await dispatch(
          addRoleMenuThunk({
            roleid: roleId,
            menuid: menuId,
          })
        );
        if (!res?.success) {
          toast.error(res?.message || "Failed to add role menu");
          return;
        }
        toast.success("Role menu added successfully!");
        await dispatch(fetchRoleMenusThunk({
          menuid: selectedMenuId || 0,
          roleid: selectedRoleId || 0,
        }));
      } catch (err) {
        console.error("Error saving role menu:", err);
        toast.error("An error occurred. Please try again.");
      }
    },
  });

  const toggleModal = () => {
    if (!isModalOpen) {
      validation.resetForm({
        values: {
          roleid: "",
          menuid: "",
        },
      });
    }
    setIsModalOpen(!isModalOpen);
  };

  const handleRowClick = useCallback((row: any) => {}, []);

  const columns = useMemo(
    () => [
      {
        header: "Role Menu ID",
        accessorKey: "role_menuid",
        cell: ({ row }: any) => {
          const id = row.original.role_menuid;
          return `#ID${String(id).padStart(3, "0")}`;
        },
      },
      { header: "Role Name", accessorKey: "rolename" },
      { header: "Menu Name", accessorKey: "menuname" },
      {
        header: "Created On",
        accessorKey: "createdon",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          return value ? (
            <>
              {handleValidDate(value)},{" "}
              <small className="text-muted">{handleValidTime(value)}</small>
            </>
          ) : (
            <>N/A</>
          );
        },
      },
      { header: "Created By", accessorKey: "createdby" },
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        cell: (cellProps: any) => {
          const row = cellProps.row.original;
  
          return (
            <div className="d-flex justify-content-center align-items-center">
              <Button
                color=""
                className="btn btn-icon btn-soft-danger btn-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(row.role_menuid);  
                }}
                title="Delete"
              >
                <i className="ri-delete-bin-line"></i>
              </Button>
            </div>
          );
        },
      },
    ],
    [handleDeleteClick, userPermissions]
  );

  return (
    <div className="page-content">
      <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
        <BreadCrumb title="Role Menu Master" pageTitle={["RBAC", "Role Menu Master"]} />

        <Row className="align-items-center g-3 d-flex justify-content-between pb-4 pe-3">
          <Col md={4}>
            <div style={{ width: "100%" }}>
              <Select
                options={roleOptions}
                placeholder="Select Role"
                value={roleOptions.find((opt) => opt.value === selectedRoleId) || null}
                onChange={handleRoleChange}
                classNamePrefix="react-select"
                isClearable
              />
            </div>
          </Col>
          <Col md={4}>
            <div style={{ width: "100%" }}>
              <Select
                options={menuOptions}
                placeholder="Select Menu"
                value={menuOptions.find((opt) => opt.value === selectedMenuId) || null}
                onChange={handleMenuChange}
                classNamePrefix="react-select"
                isClearable
              />
            </div>
          </Col>
          <Col md={4} className="text-end d-flex align-items-center justify-content-end">
            <Button color="primary" onClick={toggleModal}>
              + Add New
            </Button>
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card className="border-0 shadow-none">
              <CardBody>
                {!loading ? (
                  roleMenuList.length > 0 ? (
                    <RoleMenuContainer
                      columns={columns}
                      data={roleMenuList}
                      customPageSize={10000}
                      limit={10000}
                      setPage={() => {}}
                      onRowClick={handleRowClick}
                      tableClass="align-middle border-bottom rounded-pill"
                      theadClass="bg-primary text-white"
                      divClass="table-responsive table-card"
                    />
                  ) : (
                    <div className="text-center p-4">
                      No records found.
                    </div>
                  )
                ) : (
                  <Loader />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add Modal */}
      <Modal isOpen={isModalOpen} toggle={toggleModal} centered size="lg">
        <ModalHeader toggle={toggleModal}>
          Add New Role Menu
        </ModalHeader>
        <form onSubmit={validation.handleSubmit}>
          <ModalBody>
            <RoleMenuForm 
              validation={validation}
              menus={menus}
              roles={roles}
              roleMenus={roleMenuList}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              type="submit"
              color="primary"
            >
              Add
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <NewDeleteModal
        show={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Role Menu"
        message="Are you sure you want to delete this Role Menu?"
        onConfirm={async () => {
          if (selectedRoleMenuId) {
            setDeleteModalOpen(false);
            const role_menuid = selectedRoleMenuId;
            setSelectedRoleMenuId(null);
            try {
              const res = await dispatch(
                deleteRoleMenuThunk(role_menuid)
              );
              if (!res?.success) {
                toast.error(res?.message || "Failed to delete role menu");
                return;
              }
              toast.success("Role menu deleted successfully!");
              await dispatch(fetchRoleMenusThunk({
                menuid: selectedMenuId || 0,
                roleid: selectedRoleId || 0,
              }));
            } catch (err) {
              console.error("Error deleting role menu:", err);
              toast.error("An error occurred. Please try again.");
            }
          }
        }}
      />
    </div>
  );
};

export default RoleMenuMasterPage;
